import React, { useState } from 'react';
import { ArrowRight, List, X, CaretDown } from '@phosphor-icons/react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../assets/images/logo_black.png';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [optimizationDropdownOpen, setOptimizationDropdownOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: t('header.websites'), href: '/webdesign' },
    { name: t('header.webshops'), href: '/webshops' },
    { name: t('header.portfolio'), href: '/portfolio' },
    { name: t('header.partner'), href: '/partner' },
    { name: t('header.aboutUs'), href: '/about' },
    { name: t('header.contact'), href: '/contact' },
  ];

  const optimizationDropdownItems = [
    { name: t('header.seo'), href: '/seo' },
    { name: t('header.websiteSpeed'), href: '/website-snelheid' },
  ];


  const mobileMenuVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.07, delayChildren: 0.2 } 
    }
  };

  const mobileLinkVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { type: 'spring', stiffness: 300, damping: 25 }
    }
  };

  return (
    <>
      <header className="bg-white/80 backdrop-blur-md sticky top-0 z-[100] border-b border-gray-100 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="text-3xl font-bold text-gray-900 flex items-center group">
              <img src={logo} alt="Juliux Logo" className="h-7 mr-2 transition-transform group-hover:scale-105" />
              <span className="text-blue-500 group-hover:text-blue-600 transition-colors">•</span>
            </Link>
            <nav className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link, index) => (
                <React.Fragment key={link.name}>
                  <Link
                    to={link.href}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 relative group ${
                      location.pathname === link.href
                        ? 'bg-blue-100 text-blue-700 font-semibold'
                        : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                    }`}
                  >
                    {link.name}
                    {location.pathname === link.href && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full"></div>
                    )}
                    <div className="absolute inset-0 bg-blue-100 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                  </Link>
                  
                  {/* Insert Optimization dropdown after Webshops */}
                  {link.href === '/webshops' && (
                    <div className="relative">
                      <button
                        onMouseEnter={() => setOptimizationDropdownOpen(true)}
                        onMouseLeave={() => setOptimizationDropdownOpen(false)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 relative group flex items-center ${
                          optimizationDropdownItems.some(item => location.pathname === item.href)
                            ? 'bg-blue-100 text-blue-700 font-semibold'
                            : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
                        }`}
                      >
                        {t('header.optimization')}
                        <CaretDown weight="thin" className="w-4 h-4 ml-1" />
                        {optimizationDropdownItems.some(item => location.pathname === item.href) && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 rounded-full"></div>
                        )}
                        <div className="absolute inset-0 bg-blue-100 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                      </button>
                      
                      <AnimatePresence>
                        {optimizationDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            onMouseEnter={() => setOptimizationDropdownOpen(true)}
                            onMouseLeave={() => setOptimizationDropdownOpen(false)}
                            className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50"
                          >
                            {optimizationDropdownItems.map((item) => (
                              <Link
                                key={item.name}
                                to={item.href}
                                className={`block px-4 py-3 text-sm transition-all duration-200 ${
                                  location.pathname === item.href
                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                                }`}
                              >
                                {item.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </nav>
            <div className="hidden lg:flex items-center gap-2">
              <Link
                to="/start-your-project"
                className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-full group focus:outline-none transition-all duration-300 hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span>{t('header.startYourProject')}</span>
                <ArrowRight weight="thin" className="w-4 h-4 ml-2 transition-transform duration-300 ease-in-out group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="lg:hidden">
              <button onClick={() => setMobileMenuOpen(true)} className="text-gray-800">
                <List size={28} />
              </button>
            </div>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 35 }}
            className="fixed inset-0 bg-white z-[110] lg:hidden"
          >
            <div className="container mx-auto px-4 h-full flex flex-col">
              <div className="flex justify-between items-center py-4">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-3xl font-bold text-gray-900">
                  <img src={logo} alt="Juliux Logo" className="h-7" />
                </Link>
                <button onClick={() => setMobileMenuOpen(false)} className="text-gray-800">
                  <X size={28} />
                </button>
              </div>
              <motion.nav
                variants={mobileMenuVariants}
                initial="hidden"
                animate="visible"
                className="flex flex-col items-center justify-center flex-grow"
              >
                <div className="flex flex-col items-center space-y-6">
                  {navLinks.map((link) => (
                    <React.Fragment key={link.name}>
                      <motion.div variants={mobileLinkVariants}>
                        <Link
                          to={link.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="text-4xl font-light text-gray-800 hover:text-black transition-colors"
                        >
                          {link.name}
                        </Link>
                      </motion.div>
                      
                      {/* Insert Mobile Optimization section after Webshops */}
                      {link.href === '/webshops' && (
                        <motion.div variants={mobileLinkVariants} className="flex flex-col items-center space-y-4">
                          <div className="text-4xl font-light text-gray-800">
                            {t('header.optimization')}
                          </div>
                          <div className="flex flex-col items-center space-y-3">
                            {optimizationDropdownItems.map((item) => (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setMobileMenuOpen(false)}
                                className="text-2xl font-light text-gray-600 hover:text-gray-800 transition-colors"
                              >
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <motion.div variants={mobileLinkVariants} className="absolute bottom-16">
                  <Link
                    to="/start-your-project"
                    onClick={() => setMobileMenuOpen(false)}
                    className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-gray-900 rounded-full group transition-colors duration-300 hover:bg-gray-800"
                  >
                    <span>{t('header.startYourProject')}</span>
                    <ArrowRight weight="thin" className="w-5 h-5 ml-2 transition-transform duration-300 ease-in-out group-hover:translate-x-1" />
                  </Link>
                </motion.div>
              </motion.nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header; 